/**
 * draft-ticket-format — Helpers purs de formatage des bons provisoires.
 *
 * Aucune dépendance infra (pas de supabase, pas de device) → testable isolément,
 * même rôle que escpos-formatter pour les tickets fiscaux. La logique de
 * transport vit dans draft-printing.ts.
 */

/** Ligne d'article normalisée pour l'impression d'un bon. */
export type DraftPrintItem = {
  name: string;
  quantity: number;
  unit_price: number;
  tva_rate: number;
  /**
   * product_id du catalogue — persisté dans le snapshot jsonb depuis lot 6 pour
   * permettre la reconstruction du panier (DraftOrderPickerButton).
   * Absent sur les bons créés avant lot 6 (undefined acceptable).
   */
  product_id?: string;
};

/** Données minimales d'un bon de commande à imprimer (découplé du Row DB). */
export type DraftOrderPrintInput = {
  draft_number: string | null;
  label: string | null;
  items: unknown;
  total: number;
  created_at: string;
  register_id: string | null;
};

export type DraftTotals = {
  tvaBreakdown: Array<{ rate: number; ht: number; tva: number; ttc: number }>;
  totalHt: number;
  totalTva: number;
  totalTtc: number;
};

/**
 * Normalise le champ jsonb `items` (snapshot de panier, forme libre) en lignes
 * exploitables. Accepte les alias courants (name/product_name, unit_price/price).
 */
export function normalizeDraftItems(raw: unknown): DraftPrintItem[] {
  if (!Array.isArray(raw)) return [];
  const out: DraftPrintItem[] = [];
  for (const entry of raw) {
    if (typeof entry !== 'object' || entry === null) continue;
    const it = entry as Record<string, unknown>;
    const name = typeof it.name === 'string' ? it.name : String(it.product_name ?? 'Article');
    const quantity = Number(it.quantity ?? it.qty ?? 1) || 1;
    const unit_price = Number(it.unit_price ?? it.price ?? 0) || 0;
    const tva_rate = Number(it.tva_rate ?? it.tvaRate ?? 0) || 0;
    const product_id = typeof it.product_id === 'string' ? it.product_id : undefined;
    out.push({ name, quantity, unit_price, tva_rate, product_id });
  }
  return out;
}

/**
 * Agrège les lignes par taux de TVA. `fallbackTtc` (draft.total) fait foi pour
 * le TTC affiché — les lignes ne servent qu'au détail informatif.
 */
export function computeDraftTotals(items: DraftPrintItem[], fallbackTtc: number): DraftTotals {
  const tvaMap = new Map<number, { ht: number; tva: number; ttc: number }>();
  for (const it of items) {
    const rate = it.tva_rate;
    const lineTtc = it.unit_price * it.quantity;
    const lineHt = rate > 0 ? lineTtc / (1 + rate / 100) : lineTtc;
    const cur = tvaMap.get(rate) ?? { ht: 0, tva: 0, ttc: 0 };
    cur.ht += lineHt;
    cur.tva += lineTtc - lineHt;
    cur.ttc += lineTtc;
    tvaMap.set(rate, cur);
  }
  const tvaBreakdown = Array.from(tvaMap.entries())
    .sort(([a], [b]) => a - b)
    .map(([rate, v]) => ({ rate, ht: v.ht, tva: v.tva, ttc: v.ttc }));
  const totalHt = tvaBreakdown.reduce((s, t) => s + t.ht, 0);
  const totalTva = tvaBreakdown.reduce((s, t) => s + t.tva, 0);
  const linesTtc = tvaBreakdown.reduce((s, t) => s + t.ttc, 0);
  const totalTtc = Number(fallbackTtc) > 0 ? Number(fallbackTtc) : linesTtc;
  return { tvaBreakdown, totalHt, totalTva, totalTtc };
}
